import { Link, useNavigate } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryShortcuts from "@/pages/home/components/CategoryShortcuts";
import HotRanking from "@/pages/home/components/HotRanking";
import SectionHeading from "@/components/feature/SectionHeading";
import MovieGrid from "@/components/feature/MovieGrid";
import { getAllMovies, getFeaturedMovies, getHotMovies, getLatestMovies } from "@/lib/movies";
import { useSeo } from "@/hooks/useSeo";

const HIGHLIGHTS = [
  { icon: "ri-hd-line", title: "高清画质", desc: "全站影片以高清规格展示，画面细节更完整。" },
  { icon: "ri-calendar-check-line", title: "持续更新", desc: "每日补充最新上映与经典片单，片库不断丰富。" },
  { icon: "ri-device-line", title: "多端适配", desc: "手机、平板与桌面均可流畅浏览，随手继续看。" },
  { icon: "ri-bookmark-3-line", title: "收藏同步", desc: "一键收藏喜欢的影片，本地保存，随时回到片单。" },
];

export default function Home() {
  useSeo({
    title: "免费看大片-最新热门电影高清视频在线观看平台",
    description:
      "免费看大片为您提供最新热门电影以及经典大片在线观看服务，涵盖多类型、多题材影片，让用户轻松在线观赏丰富影视内容。",
    keywords:
      "免费看大片,电影在线观看,最新电影,热门大片,高清视频,在线电影,电影平台",
  });

  const navigate = useNavigate();
  const featured = getFeaturedMovies();
  const latest = getLatestMovies(20);
  const hot = getHotMovies(8);

  const pickRandom = () => {
    const pool = getAllMovies();
    const movie = pool[Math.floor(Math.random() * pool.length)];
    if (movie) navigate(`/detail/${movie.id}`);
  };

  return (
    <>
      <HeroCarousel movies={featured} />

      <section className="border-b border-background-200/50 bg-background-100/40">
        <div className="mx-auto grid w-full max-w-[1440px] grid-cols-1 gap-4 px-4 py-6 sm:grid-cols-2 md:px-8 lg:grid-cols-4">
          {HIGHLIGHTS.map((item) => (
            <div key={item.title} className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-200/70 text-accent-400">
                <i className={`${item.icon} text-lg`} />
              </span>
              <div>
                <h3 className="font-heading text-sm font-medium text-foreground-100">
                  {item.title}
                </h3>
                <p className="mt-1 text-xs leading-relaxed text-foreground-500">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <CategoryShortcuts />

      <section className="mx-auto w-full max-w-[1440px] px-4 pb-12 md:px-8 md:pb-16">
        <SectionHeading
          eyebrow="New releases"
          title="最新上映电影"
          description="刚刚上线的新片与近期口碑之作，20 部精选，先看最新的那一批。"
          actionLabel="查看全部影片"
          actionTo="/category/全部"
        />
        <MovieGrid movies={latest} showSynopsis />
      </section>

      <HotRanking movies={hot} />

      <section className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-8 md:py-16">
        <div className="grid gap-8 rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-10 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="mb-3 flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-accent-400">
              <span className="h-3 w-0.5 rounded-full bg-accent-500" />
              About us
            </p>
            <h2 className="font-heading text-2xl font-semibold text-foreground-50 md:text-3xl">
              一个为影迷准备的片库
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-400 md:text-base">
              免费看大片专注于整理最新热门电影与经典大片，为影迷提供清晰的影片资料、类型分区和口碑榜单。我们不靠复杂操作，只用简单的分类和搜索，帮你三分钟内选定今晚要看的那一部。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-400 md:text-base">
              目前影片详情与播放区域均为前端演示版本，真实播放能力将在后续版本接入。
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to="/category/全部"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-film-line text-base" />
                浏览全部影片
              </Link>
              <button
                type="button"
                onClick={pickRandom}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-100 transition-colors hover:border-accent-500/60 hover:text-accent-300"
              >
                <i className="ri-shuffle-line text-base" />
                随便看一部
              </button>
              <Link
                to="/about"
                className="inline-flex items-center gap-1 whitespace-nowrap px-1 py-2.5 text-sm text-foreground-400 transition-colors hover:text-primary-300"
              >
                了解我们
                <i className="ri-arrow-right-line text-sm" />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {[
              { value: `${getAllMovies().length}+`, label: "在线影片" },
              { value: "12", label: "类型分区" },
              { value: "40+", label: "导演与主创" },
              { value: "每日", label: "片单更新" },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rounded-lg border border-background-200/70 bg-background-50/60 p-4 md:p-5"
              >
                <p className="font-heading text-2xl font-semibold text-primary-300 md:text-3xl">
                  {stat.value}
                </p>
                <p className="mt-1 text-xs text-foreground-500 md:text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}